import { DateTime } from 'luxon';
import {
  DAY_CHECKIN_HOUR,
  MAX_HOURS,
  MAX_NIGHTS,
  MIN_HOURS,
  OVERNIGHT_END_HOUR,
  OVERNIGHT_START_HOUR,
} from '../common/constants';
import { PriceOptions } from '../types/bookingFormTypes';

export const getDisabledTimes = (date: Date | null | undefined, minDate: Date | null | undefined) => {
  if (!date || DateTime.fromJSDate(date).hasSame(DateTime.now(), 'day')) {
    const result = [];
    const dateObj = minDate ? DateTime.fromJSDate(minDate) : DateTime.now();
    for (let hour = 0; hour <= dateObj.get('hour'); hour++) {
      result.push(dateObj.startOf('day').plus({ hour }).toJSDate());

      if (hour < dateObj.get('hour') || dateObj.get('minute') > 30) {
        result.push(dateObj.startOf('day').plus({ hour, minute: 30 }).toJSDate());
      }
    }

    return result;
  }

  return undefined;
};

interface IQueryOptions {
  priceOption: PriceOptions;
  nights?: any;
  hours?: any;
  overnightTimeFrom?: string;
}

export const getCheckoutDate = (
  checkInTime: DateTime,
  { priceOption, nights, hours, overnightTimeFrom }: IQueryOptions,
) => {
  const checkOutDate = checkInTime;
  const startHour = getHour(overnightTimeFrom, OVERNIGHT_START_HOUR);

  switch (priceOption) {
    case PriceOptions.Hour:
      return checkOutDate.plus({ hours });
    case PriceOptions.Night:
      if (checkInTime.get('hour') >= startHour) {
        return checkOutDate.startOf('day').plus({ days: 1, hours: 11 });
      } else {
        return checkOutDate.startOf('day').plus({ hours: 11 });
      }
    case PriceOptions.Day:
      return checkOutDate.startOf('day').plus({ days: nights, hours: 11 });
    default:
      throw new Error(`Unsupported price option value ${priceOption}`);
  }
};

const DEFAULT_STEP = 0.5;
export const getTimeOptions = (startHour: number, endHour: number, step: number = DEFAULT_STEP) => {
  const result = [];
  for (let ii = startHour; ii <= endHour; ii += step) {
    result.push({
      value: ii,
      label: DateTime.now()
        .set({
          hour: ii,
          minute: (ii - Math.floor(ii)) * 60,
        })
        .toFormat('HH:mm'),
    });
  }

  return result;
};

const OVERNIGHT_CHECKIN_START = '22:00';
const OVERNIGHT_CHECKIN_END = '08:00';

export const filterOvernightTime = (time: Date, { overnightTimeFrom }: ICheckinSettings = {}) => {
  const startTime = DateTime.fromFormat(overnightTimeFrom || OVERNIGHT_CHECKIN_START, 'HH:mm');
  const endTime = DateTime.fromFormat(OVERNIGHT_CHECKIN_END, 'HH:mm');
  const dt = DateTime.fromJSDate(time);

  return (
    dt.startOf('day').set({ hour: startTime.hour, minute: startTime.minute }) <= dt ||
    dt.startOf('day').set({ hour: endTime.hour, minute: endTime.minute }) >= dt
  );
};

export interface ICheckinSettings {
  overnightTimeFrom?: string;
  overnightTimeDefault?: string;
  priceOption?: PriceOptions;
}

const getHour = (time: string, defaultHour?: number) => {
  try {
    if (time) {
      return DateTime.fromFormat(time, 'HH:mm')?.hour;
    }
  } catch (err) {
    console.error(err);
  }

  return defaultHour;
};

export const getDefaultDate = (
  date?: Date,
  { overnightTimeFrom, overnightTimeDefault, priceOption }: ICheckinSettings = {},
): Date => {
  let selectedDate: DateTime = date ? DateTime.fromJSDate(date) : DateTime.now();
  const startHour = getHour(overnightTimeFrom, OVERNIGHT_START_HOUR);

  if (date) {
    selectedDate = DateTime.fromJSDate(date);
  } else if (overnightTimeDefault && priceOption === PriceOptions.Night) {
    const defaultStartHour = getHour(overnightTimeDefault, startHour);
    selectedDate = DateTime.now().startOf('day').set({ hour: defaultStartHour });
  } else {
    selectedDate = DateTime.now();
  }

  const addedMinute = Math.floor((DateTime.now().minute + 5) / 30 + 1) * 30;
  const defaultDate = DateTime.now().startOf('hour').plus({ minutes: addedMinute });
  let result = defaultDate;
  let minDate: DateTime;

  if (priceOption === PriceOptions.Night) {
    const firstCheckInDate = selectedDate.startOf('day').set({ hour: startHour });
    const lastCheckInDate = selectedDate.startOf('day').set({ hour: OVERNIGHT_END_HOUR });
    minDate = selectedDate.startOf('day').set({ hour: startHour });

    if (selectedDate > firstCheckInDate) {
      minDate = selectedDate;
    } else if (selectedDate < lastCheckInDate) {
      minDate = selectedDate;
    } else {
      minDate = firstCheckInDate;
    }
  } else if (priceOption === PriceOptions.Day) {
    minDate = selectedDate.startOf('day').set({ hour: DAY_CHECKIN_HOUR });

    if (selectedDate > minDate) {
      minDate = selectedDate;
    }
  } else {
    minDate = selectedDate;
  }

  if (minDate && result < minDate) {
    result = minDate;
  }

  return result.toJSDate();
};

export const timeOptions = new Array(24).fill(0).flatMap((_, idx) => [
  {
    value: idx,
    label: `${idx}:00`,
  },
  {
    value: idx + 0.5,
    label: `${idx}:30`,
  },
]);

export const hoursOption = new Array(MAX_HOURS - MIN_HOURS + 1).fill(0).map((_, idx) => ({
  value: idx + MIN_HOURS,
  label: `${idx + MIN_HOURS} giờ`,
}));

export const nightsOption = new Array(MAX_NIGHTS).fill(0).map((_, idx) => ({
  value: idx + 1,
  label: `${idx + 1} đêm`,
}));
